import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * Curved Arc Cards Slider
 * When scrolling down, cards glide and rotate along an arched curved trajectory.
 * Center card sits upright while left and right cards tilt along the arc.
 * Pill-and-dots slider navigation at center reflects active card state.
 * Includes silky smooth 1:1 physical dragging, inertia flick snapping,
 * and synchronized window scroll tracking.
 */
export class HorizontalPointsSlider {
  constructor() {
    this.wrapper = document.getElementById('points-pin-wrapper');
    this.cards = document.querySelectorAll('.arc-card');
    this.dots = document.querySelectorAll('.points-dot');

    if (!this.wrapper || !this.cards.length) return;

    this.cardCount = this.cards.length;
    this.currentProgress = 0;
    this.isDragging = false;
    this.hasMoved = false;
    this.isAutoSnapping = false;
    this.preventClick = false;
    this.dragTween = null;

    this.init();
    window.nbkHorizontalSlider = this;
  }

  init() {
    // Initial display setup: first card (The Architecture, idx 0) active at center
    this.updateCardPositions(0);
    this.initCardReveals();

    // 1. GSAP ScrollTrigger Pinned Scroll Animation
    this.trigger = ScrollTrigger.create({
      trigger: this.wrapper,
      start: 'center center',
      end: () => '+=' + Math.max(1600, this.cardCount * 450),
      pin: true,
      scrub: 0.8,
      anticipatePin: 1,
      invalidateOnRefresh: true,
      onUpdate: (self) => {
        // Only update from scroll if not currently dragging or running a snap animation
        if (!this.isDragging && !this.isAutoSnapping) {
          this.currentProgress = self.progress;
          this.updateCardPositions(self.progress);
        }
      },
    });

    // 2. Click Navigation on Dots
    this.dots.forEach((dot, idx) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        this.goToCard(idx);
      });
    });

    // 3. Click Navigation on Inactive Cards to center them smoothly
    this.cards.forEach((card, idx) => {
      card.addEventListener('click', (e) => {
        if (this.preventClick) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        const isInteractive = !!e.target.closest('a, button, .nbk-pill-btn');
        const activeIdx = Math.round(this.currentProgress * (this.cardCount - 1));
        if (idx !== activeIdx && !isInteractive) {
          e.preventDefault();
          e.stopPropagation();
          this.goToCard(idx);
        }
      });
    });

    // 4. Silky Smooth Drag and Touch Gesture Support
    this.initDrag();

    // 5. Resize handler to recalibrate arc dimensions
    window.addEventListener('resize', () => {
      this.updateCardPositions(this.currentProgress);
    });
  }

  /**
   * Initialize independent Chroma reveal for each Arc Card while keeping all cards 100% visible
   */
  initCardReveals() {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.cards.forEach((card) => {
        card.dataset.chromaSwept = 'true';
        gsap.set(card.querySelectorAll('.chroma-heading'), { backgroundPosition: '0% 0' });
      });
      return;
    }

    // All card content remains permanently 100% visible across the entire curved arc
    this.cards.forEach((card, idx) => {
      const chromaHeadings = card.querySelectorAll('.chroma-heading');
      if (idx === 0) {
        // Card 0 begins at center stage
        gsap.set(chromaHeadings, { backgroundPosition: '0% 0' });
        card.dataset.chromaSwept = 'true';
      } else {
        // Inactive cards prime their gradient for the sweep when reaching center
        gsap.set(chromaHeadings, { backgroundPosition: '100% 0' });
      }
    });

    // Sweep Card 0 when wrapper enters viewport
    ScrollTrigger.create({
      trigger: this.wrapper,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        if (this.cards[0] && this.cards[0].dataset.chromaSwept !== 'true') {
          this.sweepCardChroma(this.cards[0]);
        }
      },
    });

    // If wrapper is already in viewport upon initialization, sweep Card 0 immediately
    const rect = this.wrapper.getBoundingClientRect();
    if (rect.top < window.innerHeight * 0.85 && this.cards[0]) {
      this.sweepCardChroma(this.cards[0]);
    }
  }

  /**
   * Sweeps the Chroma Text Reveal across the card's heading when it reaches center stage
   */
  sweepCardChroma(card) {
    if (!card || card.dataset.chromaSwept === 'true') return;
    card.dataset.chromaSwept = 'true';

    const chromaHeadings = card.querySelectorAll('.chroma-heading');
    if (chromaHeadings.length) {
      gsap.to(chromaHeadings, {
        backgroundPosition: '0% 0',
        duration: 0.9,
        ease: 'power2.out',
        overwrite: 'auto',
      });
    }
  }

  /**
   * Get responsive physical horizontal spread between cards
   */
  getCardSpread() {
    if (window.innerWidth < 420) {
      return Math.min(260, window.innerWidth * 0.75);
    }
    const isMobile = window.innerWidth < 640;
    const isTablet = window.innerWidth < 1024;
    return isMobile ? 280 : isTablet ? 360 : Math.min(540, Math.max(450, window.innerWidth * 0.28));
  }

  /**
   * Position cards along the curved parabolic arc based on progress (0 to 1)
   */
  updateCardPositions(progress) {
    const isMobile = window.innerWidth < 640;
    const cardSpread = this.getCardSpread();
    const maxDrop = isMobile ? 36 : 58; // Dynamic parabolic drop
    const maxAngle = isMobile ? 6.5 : 9.5; // Expressive, pronounced arc curve tilt

    this.cards.forEach((card, idx) => {
      // Relative offset from current active center
      const activeIdx = progress * (this.cardCount - 1);
      const t = idx - activeIdx; // -1 for left card, 0 for center card, +1 for right card

      // Trigger Chroma reveal sweep when this card glides to the center active zone
      if (Math.abs(t) <= 0.65 && card.dataset.chromaSwept !== 'true') {
        this.sweepCardChroma(card);
      }

      const x = t * cardSpread;
      const y = Math.pow(Math.abs(t), 1.5) * maxDrop;
      const rot = t * maxAngle;
      const scale = Math.max(0.91, 1 - Math.abs(t) * 0.045); // Retain >= 91% scale for crystal-clear subpixel font rendering
      const opacity = Math.max(0.68, 1 - Math.abs(t) * 0.15); // High readability across all adjacent cards
      const zIndex = Math.round(50 - Math.abs(t) * 15);

      card.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) rotate(${rot.toFixed(2)}deg) scale(${scale.toFixed(3)})`;
      card.style.zIndex = zIndex;
      card.style.opacity = opacity.toFixed(2);
    });

    // Update Pill & Dots Slider Navigation
    this.updateDots(progress);
  }

  updateDots(progress) {
    if (!this.dots || !this.dots.length) return;
    const activeIdx = Math.min(
      this.cardCount - 1,
      Math.max(0, Math.round(progress * (this.cardCount - 1))),
    );

    this.dots.forEach((dot, idx) => {
      const isSelected = idx === activeIdx;
      dot.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      const inner = dot.querySelector('.points-dot-inner') || dot;
      if (isSelected) {
        inner.className =
          'points-dot-inner w-8 sm:w-10 h-2 sm:h-2.5 rounded-full bg-white shadow-[0_0_12px_rgba(255,255,255,0.85)] transition-all duration-300 block pointer-events-none';
      } else {
        inner.className =
          'points-dot-inner w-2 sm:w-2.5 h-2 sm:h-2.5 rounded-full bg-white/30 hover:bg-white/60 transition-all duration-300 block pointer-events-none';
      }
    });
  }

  /**
   * Smoothly navigate to a specific card index with spring easing and scroll sync
   */
  goToCard(targetIndex, smooth = true) {
    const clampedIndex = Math.max(0, Math.min(this.cardCount - 1, targetIndex));
    const targetProgress = clampedIndex / (this.cardCount - 1);

    const targetCard = this.cards[clampedIndex];
    if (targetCard && targetCard.dataset.chromaSwept !== 'true') {
      this.sweepCardChroma(targetCard);
    }

    if (this.dragTween) this.dragTween.kill();

    if (!smooth) {
      this.currentProgress = targetProgress;
      this.updateCardPositions(targetProgress);
      this.syncScroll(targetProgress);
      return;
    }

    this.isAutoSnapping = true;
    this.dragTween = gsap.to(this, {
      currentProgress: targetProgress,
      duration: 0.48,
      ease: 'power3.out',
      onUpdate: () => {
        this.updateCardPositions(this.currentProgress);
      },
      onComplete: () => {
        this.isAutoSnapping = false;
        this.syncScroll(targetProgress);
      },
    });
  }

  /**
   * Synchronize window / Lenis scroll position with the current active card progress
   */
  syncScroll(targetProgress) {
    if (!this.trigger) return;
    const start = this.trigger.start;
    const end = this.trigger.end;
    const targetScroll = start + targetProgress * (end - start);

    if (window.nbkLenis) {
      window.nbkLenis.scrollTo(targetScroll, { immediate: true });
    } else {
      window.scrollTo({ top: targetScroll, behavior: 'instant' });
    }
  }

  /**
   * 1:1 Physical Drag and Touch Gesture Controller with Momentum and Click Suppression
   */
  initDrag() {
    let startX = 0;
    let startY = 0;
    let startProgress = 0;
    let velocityBuffer = [];

    // Global capture click suppressor to prevent accidental button clicks on drag release
    const captureClick = (e) => {
      if (this.preventClick) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    this.wrapper.addEventListener('click', captureClick, { capture: true });

    const getNow = () => (window.performance ? window.performance.now() : Date.now());

    const onPointerDown = (e) => {
      // Allow only primary mouse button or touch
      if (e.button !== undefined && e.button !== 0) return;

      // Do NOT intercept or capture pointer on interactive links, buttons, or form controls
      if (e.target.closest('a, button, .nbk-pill-btn, input, textarea, select')) {
        return;
      }

      if (this.dragTween) this.dragTween.kill();
      this.isDragging = true;
      this.hasMoved = false;
      this.preventClick = false;

      startX = e.clientX;
      startY = e.clientY;
      startProgress = this.currentProgress;
      velocityBuffer = [{ x: e.clientX, t: getNow() }];

      this.wrapper.style.cursor = 'grabbing';

      try {
        this.wrapper.setPointerCapture(e.pointerId);
      } catch {
        // Fallback for non-supporting environments
      }
    };

    const onPointerMove = (e) => {
      if (!this.isDragging) return;

      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;

      if (!this.hasMoved) {
        const dist = Math.hypot(deltaX, deltaY);
        if (dist < 7) return;

        // If movement is predominantly vertical, release drag so native page scroll continues
        if (Math.abs(deltaY) > Math.abs(deltaX) * 1.35) {
          this.isDragging = false;
          this.wrapper.style.cursor = '';
          try {
            this.wrapper.releasePointerCapture(e.pointerId);
          } catch {
            // Fallback
          }
          return;
        }

        this.hasMoved = true;
        this.preventClick = true;
      }

      // Record velocity
      const now = getNow();
      velocityBuffer.push({ x: e.clientX, t: now });
      if (velocityBuffer.length > 5) velocityBuffer.shift();

      // Physical 1:1 card distance mapping
      const cardSpread = this.getCardSpread();
      const progressDelta = -(deltaX / cardSpread) / (this.cardCount - 1);
      let targetProgress = startProgress + progressDelta;

      // Soft elastic resistance beyond boundaries [0, 1]
      if (targetProgress < 0) {
        targetProgress = targetProgress * 0.3;
      } else if (targetProgress > 1) {
        targetProgress = 1 + (targetProgress - 1) * 0.3;
      }

      this.currentProgress = targetProgress;
      this.updateCardPositions(targetProgress);
    };

    const onPointerUp = (e) => {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.wrapper.style.cursor = '';

      try {
        this.wrapper.releasePointerCapture(e.pointerId);
      } catch {
        // Fallback
      }

      if (!this.hasMoved) {
        this.preventClick = false;
        return;
      }

      // Calculate release velocity from recent buffer
      let vx = 0;
      if (velocityBuffer.length >= 2) {
        const first = velocityBuffer[0];
        const last = velocityBuffer[velocityBuffer.length - 1];
        const dt = last.t - first.t;
        if (dt > 8) {
          vx = (last.x - first.x) / dt; // px per ms
        }
      }

      const activeFloat = this.currentProgress * (this.cardCount - 1);
      let targetIndex = Math.round(activeFloat);

      // Flick gesture: swipe with speed advances or retreats
      if (Math.abs(vx) > 0.35) {
        if (vx < -0.35) {
          targetIndex = Math.ceil(activeFloat);
          if (targetIndex === Math.floor(activeFloat)) targetIndex += 1;
        } else if (vx > 0.35) {
          targetIndex = Math.floor(activeFloat);
          if (targetIndex === Math.ceil(activeFloat)) targetIndex -= 1;
        }
      }

      targetIndex = Math.max(0, Math.min(this.cardCount - 1, targetIndex));
      this.goToCard(targetIndex, true);

      // Keep click prevented briefly to consume trailing click event
      setTimeout(() => {
        this.preventClick = false;
      }, 140);
    };

    this.wrapper.addEventListener('pointerdown', onPointerDown);
    this.wrapper.addEventListener('pointermove', onPointerMove);
    this.wrapper.addEventListener('pointerup', onPointerUp);
    this.wrapper.addEventListener('pointercancel', onPointerUp);
  }
}
